import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { ArrowLeft, Plus, RefreshCw } from 'lucide-react';
import {
  Button,
  FormSelect,
  Heading,
  Input,
  PageLoader,
  Sheet,
  SheetContent,
  State,
} from '@/components/atoms';
import { PageHeader } from '@/components/molecules';
import { useSyncWhatsAppTemplates, useWhatsAppTemplates } from '@/api';
import { routes } from '@/lib/constants';
import {
  WHATSAPP_TEMPLATE_CATEGORIES,
  WHATSAPP_TEMPLATE_STATUSES,
  WHATSAPP_TEMPLATES_PAGE_SIZE,
} from '@/lib/constants/whatsapp-templates';
import type { WhatsAppTemplate } from '@/types/whatsapp-template';
import {
  TemplateDetailSheet,
  TemplateForm,
  TemplatesTable,
} from './components';

export const ConversationTemplatesPage = () => {
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('all');
  const [page, setPage] = useState(1);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [viewTarget, setViewTarget] = useState<WhatsAppTemplate | null>(null);

  const listParams = useMemo(
    () => ({
      page,
      limit: WHATSAPP_TEMPLATES_PAGE_SIZE,
      search: search.trim() || undefined,
      category: category !== 'all' ? category : undefined,
      status: status !== 'all' ? status : undefined,
    }),
    [page, search, status, category]
  );

  const { data, isLoading, isError, error, isFetching } =
    useWhatsAppTemplates(listParams);

  const { mutate: syncTemplates, isPending: isSyncing } =
    useSyncWhatsAppTemplates();

  const templates = data?.templates ?? [];
  const lastPage = data?.lastPage ?? 1;
  const total = data?.total ?? 0;

  const openCreate = () => setSheetOpen(true);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4">
        <Button variant="link" className="w-fit px-0" asChild>
          <Link to={routes.conversations}>
            <ArrowLeft className="size-4" />
            Back to conversations
          </Link>
        </Button>

        <div className="flex max-md:flex-col items-start max-md:gap-4 justify-between gap-4">
          <PageHeader
            heading="WhatsApp templates"
            description="Create and manage reusable WhatsApp message templates."
          />
          <div className="flex flex-wrap gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => syncTemplates()}
              isLoading={isSyncing}
            >
              <RefreshCw className="size-4" />
              Sync templates
            </Button>
            <Button onClick={openCreate}>
              <Plus />
              Create template
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
        <Input
          value={search}
          onChange={(event) => {
            setSearch(event.target.value);
            setPage(1);
          }}
          placeholder="Search templates…"
          className="border-none bg-muted/30 xl:col-span-2"
        />
        <FormSelect
          placeholder="All statuses"
          value={status}
          onChange={(value) => {
            setStatus(value);
            setPage(1);
          }}
          options={[
            { value: 'all', label: 'All statuses' },
            ...WHATSAPP_TEMPLATE_STATUSES.map((item) => ({
              value: item.value,
              label: item.label,
            })),
          ]}
        />
        <FormSelect
          placeholder="All categories"
          value={category}
          onChange={(value) => {
            setCategory(value);
            setPage(1);
          }}
          options={[
            { value: 'all', label: 'All categories' },
            ...WHATSAPP_TEMPLATE_CATEGORIES.map((item) => ({
              value: item,
              label: item,
            })),
          ]}
        />
      </div>

      {isLoading ? (
        <PageLoader message="Loading templates…" className="py-20" />
      ) : isError ? (
        <State
          description={
            error instanceof Error
              ? error.message
              : 'Could not load templates. Please try again.'
          }
          button={{
            icon: 'RefreshCw',
            text: 'Retry',
            onClick: () => window.location.reload(),
          }}
        />
      ) : (
        <>
          <TemplatesTable
            templates={templates}
            onCreate={openCreate}
            onView={setViewTarget}
          />

          {total > 0 ? (
            <div className="flex items-center justify-between gap-4 text-sm text-muted-foreground">
              <span>
                {total} template{total === 1 ? '' : 's'}
                {isFetching ? ' · Refreshing…' : ''}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((current) => Math.max(1, current - 1))}
                >
                  Previous
                </Button>
                <span>
                  Page {page} of {lastPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= lastPage}
                  onClick={() => setPage((current) => current + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          ) : null}
        </>
      )}

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-4xl overflow-y-auto pt-10 pb-6 px-4 md:px-6">
          <Heading size="xl">Create template</Heading>
          <TemplateForm onSuccess={() => setSheetOpen(false)} />
        </SheetContent>
      </Sheet>

      <TemplateDetailSheet
        template={viewTarget}
        open={!!viewTarget}
        onOpenChange={(open) => !open && setViewTarget(null)}
      />
    </div>
  );
};
